import { Component, Input, OnChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { GetPpgFromValue, GetValueFromcP, GetValueFromFahrenheit, GetValueFromPpg, GetYpValueFromlbf } from 'src/app/perivis/shared/helpers/units.helper';

export interface StandardMudForm {
  name: string;
  type: string;
  baseFluid: string;
  nominalDensity: number;
  densityTemperature: number;
  plasticViscosity: number;
  rheologyTemperature: number;
  yieldPoint: number;
}

@Component({
    selector: 'app-standard-muds',
    templateUrl: './standard-muds.component.html',
    styles: [``],
    providers: subformComponentProviders(StandardMudsComponent),
    standalone: false
})
export class StandardMudsComponent implements OnChanges {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }
  
  public pvDefault = 0.56;
  public ypDefault = 0;
  public tempUnit: string;
  public pvUnits: string;
  public pvValidation: { min: number, max: number };
  public ypUnits: string;
  public ypValidation: { min: number, max: number };
  public temperatureValidation: { min: number, max: number };
  public densityUnits: string;
  public densityValidation: { min: number, max: number };

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public isReadOnly: boolean;

  public baseFluids = [
    { label: 'Freshwater', value: 'freshwater' },
    { label: 'Seawater', value: 'seawater'},
    { label: 'Diesel', value: 'no2Diesel' }
  ];

  public form = createForm<StandardMudForm>(this, {
    formType: FormType.SUB,
    formControls: {
      name: new UntypedFormControl(null),
      type: new UntypedFormControl(null),
      baseFluid: new UntypedFormControl(null, [Validators.required]),
      nominalDensity: new UntypedFormControl(null, { updateOn: 'blur' }),
      densityTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      plasticViscosity: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(2000)] }),
      rheologyTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      yieldPoint: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(2000)] })
    }
  });

  ngOnChanges(): void {
    if (!this.userUnits) {
      return;
    }
    this.tempUnit = this.userUnits.temperature;
    this.densityUnits = unitsLib[this.userUnits.density].symbol;
    this.pvUnits = this.userUnits.plasticViscosity;
    this.ypUnits = this.userUnits.yieldPoint;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 1000 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.7, max: 537 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255, max: 810 };
        break;
    }

    this.form.formGroup.controls.densityTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);
    this.form.formGroup.controls.rheologyTemperature.setValidators([Validators.required, Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max)]);

    switch (this.densityUnits) {
      case 'ppg':
        this.densityValidation = { min: 0.01, max: 25 };
        break;
      case 'kg/m³':
      case 'g/L':
        this.densityValidation = { min: 1, max: 2500 };
        break;
      case 'g/m³':
        this.densityValidation = { min: 1198, max: 2995660 };
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.densityValidation = { min: 0.00119, max: 3 };
        break;
    }

    this.form.formGroup.controls.nominalDensity.setValidators([Validators.required, Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max)]);

    switch (this.pvUnits) {
      case 'cP':
        this.pvValidation = { min: 0, max: 2000 };
        break;
      case 'Pa/s':
        this.pvValidation = { min: 0, max: 2 };
        break;
    }

    this.form.formGroup.controls.plasticViscosity.setValidators([Validators.required, Validators.min(this.pvValidation.min), Validators.max(this.pvValidation.max)]);

    switch (this.ypUnits) {
      case 'lbf/100ft²':
        this.ypValidation = { min: 0, max: 2000 };
        break;
      case 'Pa':
        this.ypValidation = { min: 0, max: 957.6 };
        break;
    }

    this.form.formGroup.controls.yieldPoint.setValidators([Validators.required, Validators.min(this.ypValidation.min), Validators.max(this.ypValidation.max)]);
  }

  public setDefaultPvYp(nominalDensityRef, isDropdown): void {
    if (!nominalDensityRef && !isDropdown) {
      nominalDensityRef = this.form.formGroup.controls.nominalDensity;
    }
    const baseFluid = isDropdown ? nominalDensityRef.value : this.form.formGroup.controls.baseFluid.value;
    const density = isDropdown ? GetPpgFromValue(this.form.formGroup.controls.nominalDensity.value, this.densityUnits) : GetPpgFromValue(nominalDensityRef.value, this.densityUnits);

    if ((baseFluid === 'freshwater' && density > GetValueFromPpg(8.5, this.densityUnits)) || (baseFluid === 'no2Diesel' && density > GetValueFromPpg(7.5, this.densityUnits))) {
      const pvValue = this.form.formGroup.value.plasticViscosity;
      if (isDropdown || (pvValue == this.pvDefault || Math.abs(pvValue - 9.37) < 0.01)) {
        const pv = baseFluid === 'no2Diesel' ? 2 * density - 9.98 + 2.66 : 2 * density - 9.98;
        this.form.formGroup.controls.plasticViscosity.setValue(GetValueFromcP(pv, this.pvUnits));
      }

      const ypValue = this.form.formGroup.value.yieldPoint;
      if (isDropdown || (ypValue == this.ypDefault || ypValue == 0)) {
        this.form.formGroup.controls.yieldPoint.setValue(GetYpValueFromlbf(7, this.ypUnits));
      }
    }

    if (baseFluid === 'freshwater' && isDropdown && Math.abs(density - GetValueFromPpg(8.33, this.densityUnits)) < 0.01) {
      this.form.formGroup.controls.plasticViscosity.setValue(0.56)
      this.form.formGroup.controls.yieldPoint.setValue(0);
    }
  }

  public applyDefaults(): void {
    const densityDefault = GetValueFromPpg(8.33, this.densityUnits);
    const densityTempDefault = GetValueFromFahrenheit(70, this.tempUnit);
    const rheologyTempDefault = GetValueFromFahrenheit(120, this.tempUnit);


    this.form.formGroup.controls.nominalDensity.setValue(densityDefault);
    this.form.formGroup.controls.densityTemperature.setValue(densityTempDefault);
    this.form.formGroup.controls.plasticViscosity.setValue(this.pvDefault);
    this.form.formGroup.controls.rheologyTemperature.setValue(rheologyTempDefault);
    this.form.formGroup.controls.yieldPoint.setValue(this.ypDefault);
  }
}
